import React from 'react'
import { User } from '../../../providers/context'
import {
  ConferenceAppointment,
  TeacherConferenceData,
  ConferenceGuardianBadge,
  ConferenceLocation,
  ConferenceLocationEdit,
  NoteEditor,
} from '../components/conferenceComponents'
import { DelayedTextInput } from '../../../components/form/textInput'
import { RemoteTable, VisibleColumnDef } from '../../../components/table'
import { Badge, BadgeStyle } from '../../../components/simple/badge'
import { IconGlobe } from '../../../icons/library'
import { makeRequest, showAxiosErrorAsToast } from '../../../request'
import { Checkbox } from '../../../components/form/checkbox'

type Appointment = {
  id: number
  date: string
  student: {
    name: string
    grade: string
    familyLanguages: string[]
    guardians: ConferenceAppointment['guardians']
  }
  teacher?: string
  virtual: boolean
  location: ConferenceLocation
  language?: {
    name?: string
    actions: {
      name: string
      url: string
    }[]
  }
  notes: string
  attended: boolean
  createdAt: string
}

export const AppointmentsTab: React.FC<{
  conference: TeacherConferenceData
  viewAsAdmin: boolean
  user: User
  editYourLocation: () => void
  showOtherTeachersAppointments?: boolean
}> = ({ conference, viewAsAdmin, user, editYourLocation, showOtherTeachersAppointments = false }) => {
  const [search, setSearch] = React.useState('')

  const tableRemote = React.useMemo(
    () => ({
      url: '/frontend_api/getConferenceAppointmentsTable',
      data: {
        conferenceID: conference.id,
        viewAsAdmin,
        showOtherTeachersAppointments,
      },
    }),
    [conference.id, viewAsAdmin, showOtherTeachersAppointments]
  )

  const tableColumns = React.useMemo<VisibleColumnDef<Appointment>[]>(
    () => [
      { id: 'date', header: 'Date', accessorKey: 'date' },
      {
        id: 'student',
        header: 'Student',
        accessorFn: (row) => row.student.name,
        cell: ({ row }) => (
          <div className="flex flex-wrap items-center gap-2">
            {row.original.student.name}{' '}
            <Badge badgeStyle={BadgeStyle.mint} tooltip={`In grade ${row.original.student.grade}`}>
              {row.original.student.grade}
            </Badge>
            {row.original.student.familyLanguages?.length > 0 && (
              <IconGlobe tooltip={`Family language: ${row.original.student.familyLanguages?.join(', ')}`} />
            )}
            {row.original.student.guardians.map((g, i) => (
              <ConferenceGuardianBadge key={i} guardian={g} />
            ))}
          </div>
        ),
      },
      {
        id: 'teacher',
        show: viewAsAdmin || showOtherTeachersAppointments,
        header: 'Teacher',
        accessorKey: 'teacher',
      },
      {
        id: 'location',
        header: 'Location',
        enableSorting: false,
        cell: ({ row }) => (
          <ConferenceLocation
            virtualOrInPerson={row.original.virtual ? 'virtual' : 'inperson'}
            location={row.original.location}
          />
        ),
        accessorFn: (row) => (row.virtual ? row.location.url : `Room ${row.location.roomNumber}`),
      },
      {
        show: conference.multilingual,
        id: 'language',
        header: 'Language',
        accessorFn: (row) =>
          (row.language?.name || '') +
          ' ' +
          (row.language?.actions.map((a) => `${a.name} (${a.url})`).join(', ') || ''),
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.language?.name || ''}
            {row.original.language?.actions.map((a) => (
              <a key={a.url} href={a.url} target="_blank" className="text-blue-500">
                {a.name}
              </a>
            ))}
          </div>
        ),
      },
      {
        id: 'notes',
        header: 'Notes',
        accessorKey: 'notes',
        cell: ({ row }) =>
          showOtherTeachersAppointments ? (
            row.original.notes
          ) : (
            <NoteEditor showFullText note={row.original.notes} appointmentID={row.original.id} />
          ),
      },
      {
        id: 'attended',
        header: 'Attended',
        accessorFn: (row) => (row.attended ? 'Yes' : 'No'),
        cell: ({ row }) => (
          <AttendedCheckbox
            disabled={showOtherTeachersAppointments}
            appointmentID={row.original.id}
            attended={row.original.attended}
          />
        ),
      },
      {
        id: 'createdAt',
        header: 'Created At',
        accessorKey: 'createdAt',
      },
    ],
    [conference]
  )

  return (
    <div className="mt-2 flex min-h-0 flex-col gap-2">
      {!viewAsAdmin && !showOtherTeachersAppointments && (
        <ConferenceLocationEdit conference={conference} editYourLocation={editYourLocation} />
      )}
      <div className="flex items-center gap-2">
        {showOtherTeachersAppointments ? (
          conference.teachersViewAllAppointments ? (
            <b className="text-lg">All appointments</b>
          ) : (
            <b className="text-lg">Appointments with your students and other teachers</b>
          )
        ) : (
          <b className="text-lg">Your appointments</b>
        )}
        <DelayedTextInput placeholder="Search" value={search} onChange={setSearch} className="ml-auto rounded-full" />
      </div>
      <RemoteTable
        search={search}
        columns={tableColumns}
        remote={tableRemote}
        sortable
        exportName={`All ${viewAsAdmin ? '' : 'your'} appointments for ${conference.name}`}
        pagination={10}
        rowName="appointment"
      />
    </div>
  )
}

const AttendedCheckbox: React.FC<{ appointmentID: number; attended: boolean; disabled: boolean }> = ({
  appointmentID,
  attended,
  disabled,
}) => {
  const [checked, setChecked] = React.useState(attended)
  React.useEffect(() => {
    setChecked(attended)
  }, [attended, appointmentID])
  return (
    <Checkbox
      disabled={disabled}
      className="float-left" // fixes weird rendering bug
      checked={checked}
      onChecked={() => {
        makeRequest('/ptc/appointments/attended', { appt_id: appointmentID, value: !checked })
          .then(() => setChecked((c) => !c))
          .catch(showAxiosErrorAsToast)
      }}
    />
  )
}
